/**
 * Input validation shared by client and API. The API is the authority; the
 * client mirrors these rules so that a form can refuse early with the same
 * message the server would give.
 */

export interface ValidationError {
  field: string;
  message: string;
}

export const TITLE_MIN = 3;
export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 4000;
export const COMMENT_MAX = 2000;
export const MAX_IMAGES_PER_DAMAGE = 10;
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const UPLOAD_MAX_EDGE_PX = 2048;
export const THUMBNAIL_EDGE_PX = 400;

/** Mirrors the API's normalisation: trim, Unicode NFC, lowercase. */
export function normalizeEmail(input: string): string {
  return input.trim().normalize('NFC').toLowerCase();
}

/**
 * Stricter than the browser's type=email: one @, something on both sides, a dot
 * in the domain, no whitespace. Deliberately not RFC 5322; the goal is to catch
 * typos before a code is sent to nowhere.
 */
export function isValidEmail(input: string): boolean {
  const email = normalizeEmail(input);
  if (email.length < 6 || email.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

/** D-XXXX with letters, or D-#### for gliders. */
export function isValidRegistration(input: string): boolean {
  return /^D-(?:[A-Z]{4}|\d{4})$/.test(input.trim().toUpperCase());
}

export function validateDamageTitle(title: string): ValidationError | null {
  const t = title.trim();
  if (t.length < TITLE_MIN)
    return { field: 'title', message: `Titel braucht mindestens ${TITLE_MIN} Zeichen.` };
  if (t.length > TITLE_MAX)
    return { field: 'title', message: `Titel darf höchstens ${TITLE_MAX} Zeichen haben.` };
  return null;
}

export function validateDescription(
  description: string | null | undefined,
): ValidationError | null {
  if (description && description.length > DESCRIPTION_MAX) {
    return {
      field: 'description',
      message: `Beschreibung darf höchstens ${DESCRIPTION_MAX} Zeichen haben.`,
    };
  }
  return null;
}

export function validateCoordinate(
  value: number,
  field: 'pos_x' | 'pos_y',
): ValidationError | null {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    return { field, message: 'Position liegt außerhalb der Zeichnung.' };
  }
  return null;
}

/** Six digits, leading zeros allowed. */
export function isValidLoginCode(input: string): boolean {
  return /^\d{6}$/.test(input.trim());
}
